import { useMemo } from "react";
import { Provider } from "@/types/supabase";
import { isProviderVerified } from "@/utils/verificationHelpers";
import { getProviderCategoryKeys } from "@/utils/providerCategories";

interface FilteredProvidersProps {
  providers: Provider[];
  searchQuery: string;
  appliedFilters: {
    categories: string[];
    suburbs: string[];
    rating: string;
    verifiedOnly: boolean;
    showFavorites: boolean;
    topRatedOnly: boolean;
    featuredOnly: boolean;
  };
  sortOption: string;
  allReviews: Record<string, Array<{ rating: number; is_public?: boolean }>>;
  favorites: string[];
  userId?: string;
}

export function useFilteredProviders({
  providers,
  searchQuery,
  appliedFilters,
  sortOption,
  allReviews,
  favorites,
  userId
}: FilteredProvidersProps) {
  return useMemo(() => {
    return providers.filter(provider => {
      const searchTerms = searchQuery.toLowerCase();
      const providerCategoryKeys = getProviderCategoryKeys(provider);

      const searchMatch = 
        searchQuery === "" ||
        provider.name.toLowerCase().includes(searchTerms) ||
        providerCategoryKeys.some(category => category.includes(searchTerms)) ||
        provider.description.toLowerCase().includes(searchTerms) ||
        (provider.suburbs && provider.suburbs.some(suburb => suburb.toLowerCase().includes(searchTerms)));
      
      const categoryMatch = appliedFilters.categories.length === 0 || 
        appliedFilters.categories.some(category => 
          providerCategoryKeys.includes(category.toLowerCase())
        );
      
      const suburbMatch = appliedFilters.suburbs.length === 0 || 
        appliedFilters.suburbs.some(suburb => 
          provider.suburbs && provider.suburbs.some(providerSuburb => 
            providerSuburb.toLowerCase() === suburb.toLowerCase()
          )
        );
      
      const ratingMatch = !appliedFilters.rating || 
        provider.rating >= parseInt(appliedFilters.rating);
      
      const verifiedMatch = !appliedFilters.verifiedOnly || 
        isProviderVerified(provider, provider.user_profile_verified);
      
      const favoritesMatch = !appliedFilters.showFavorites || 
        (userId && favorites.includes(provider.id));
      
      const featuredMatch = !appliedFilters.featuredOnly || provider.is_featured;
      
      let topRatedMatch = true;
      if (appliedFilters.topRatedOnly) {
        const reviews = allReviews[provider.id] || [];
        const publicReviews = reviews.filter(review => review.is_public !== false);
        
        if (publicReviews.length === 0) {
          topRatedMatch = false;
        } else {
          const totalRating = publicReviews.reduce((sum, review) => sum + review.rating, 0);
          const averageRating = totalRating / publicReviews.length;
          topRatedMatch = averageRating > 4.5 && publicReviews.length > 0;
        }
      }
      
      return searchMatch && categoryMatch && suburbMatch && 
        ratingMatch && verifiedMatch && favoritesMatch && featuredMatch && topRatedMatch;
    }).sort((a, b) => {
      // Helper function to get average rating
      const getAvgRating = (providerId: string) => {
        const reviews = allReviews[providerId] || [];
        const publicReviews = reviews.filter(review => review.is_public !== false);
        
        if (publicReviews.length === 0) return 0;
        
        return publicReviews.reduce((sum, review) => sum + review.rating, 0) / publicReviews.length;
      };
      
      // Helper function to get review count
      const getReviewCount = (providerId: string) => {
        const reviews = allReviews[providerId] || [];
        return reviews.filter(review => review.is_public !== false).length;
      };
      
      // Calculate once for efficiency
      const aAvgRating = getAvgRating(a.id);
      const bAvgRating = getAvgRating(b.id);
      const aReviewCount = getReviewCount(a.id);
      const bReviewCount = getReviewCount(b.id);

      switch (sortOption) {
        case "featured":
          // First by featured status
          if (a.is_featured !== b.is_featured) {
            return a.is_featured ? -1 : 1;
          }
          // Then by verified status
          if (a.is_verified !== b.is_verified) {
            return a.is_verified ? -1 : 1;
          }
          // Then by top-rated status
          if (a.is_top_rated !== b.is_top_rated) {
            return a.is_top_rated ? -1 : 1;
          }
          // Finally by average rating as a tiebreaker
          return bAvgRating - aAvgRating;
          
        case "rating":
          // First by average rating
          if (aAvgRating !== bAvgRating) {
            return bAvgRating - aAvgRating;
          }
          // For providers with no ratings or equal ratings, sort by featured status
          if (a.is_featured !== b.is_featured) {
            return a.is_featured ? -1 : 1;
          }
          // Then by verified status
          if (a.is_verified !== b.is_verified) {
            return a.is_verified ? -1 : 1;
          }
          // Then by top-rated status
          if (a.is_top_rated !== b.is_top_rated) {
            return a.is_top_rated ? -1 : 1;
          }
          // As a final tie-breaker, sort by review count
          return bReviewCount - aReviewCount;
          
        case "reviews":
          // First by number of reviews
          if (aReviewCount !== bReviewCount) {
            return bReviewCount - aReviewCount;
          }
          // For providers with equal reviews, sort by featured status
          if (a.is_featured !== b.is_featured) {
            return a.is_featured ? -1 : 1;
          }
          // Then by top-rated status
          if (a.is_top_rated !== b.is_top_rated) {
            return a.is_top_rated ? -1 : 1;
          }
          // Then by verified status
          if (a.is_verified !== b.is_verified) {
            return a.is_verified ? -1 : 1;
          }
          // As a final tie-breaker, sort by rating
          return bAvgRating - aAvgRating;
          
        case "name_asc":
          return a.name.localeCompare(b.name);
          
        case "name_desc":
          return b.name.localeCompare(a.name);
          
        default:
          // Default fallback sorting (by featured, then verified, then top rated)
          if (a.is_top_rated !== b.is_top_rated) {
            return a.is_top_rated ? -1 : 1;
          }
          return bAvgRating - aAvgRating;
      }
    });
  }, [providers, appliedFilters, searchQuery, sortOption, favorites, userId, allReviews]);
}
